﻿import dayjs from 'dayjs';
import { useCallback, useContext } from 'react';
import { shallowEqual } from 'react-redux';
import { getDirectoryDb } from '../../../../services/directory-db';
import { MapImageFormat } from '../../../../types/settings';
import { useEvent } from '../../../contexts/event-bus';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import useMap from '../../../hooks/use-map';
import { addNotification, dismissNotification, NotificationType, transformNotification } from '../../../state/slices/global-slice';

const ImageFormat: Record<MapImageFormat, string> = {
    [MapImageFormat.jpg]: 'image/jpeg',
    [MapImageFormat.png]: 'image/png',
    [MapImageFormat.webp]: 'image/webp'
};
const FileExtension: Record<MapImageFormat, string> = {
    [MapImageFormat.jpg]: '.jpg',
    [MapImageFormat.png]: '.png',
    [MapImageFormat.webp]: '.webp'
};
const MimeType: Record<string, MapImageFormat> = {
    ['.png']: MapImageFormat.png,
    ['.jpg']: MapImageFormat.jpg,
    ['.webp']: MapImageFormat.webp
};
type ImageSettings = {
    imageWidth: number | null;
    imageHeight: number | null;
    imageFormat: MapImageFormat;
    filename: string;
    appendDateToFilename: boolean;
    serverUrl: string | null;
};
export enum ScreenshotCommandType {
    save,
    saveAs,
    serverUpload,
    slideShowAutomatic
}

export type ScreenshotCommand = {
    workspaceIndex: number;
} & (
    | { type: ScreenshotCommandType.save | ScreenshotCommandType.saveAs | ScreenshotCommandType.serverUpload; customFilename?: string }
    | {
          type: ScreenshotCommandType.slideShowAutomatic;
          fileName: string;
          saveToFile: boolean;
          upload: boolean;
      }
);

export const MAP_INDEXEDDB_KEY = 'map-image';
export default function MapScreenshot() {
    const dispatch = useAppDispatch();
    const imageSettings: ImageSettings = useAppSelector(
        (state) => ({
            imageWidth: state.settings.mapImageWidth,
            imageHeight: state.settings.mapImageHeight,
            imageFormat: state.settings.mapImageFormat,
            filename: state.settings.mapImageFileName,
            appendDateToFilename: state.settings.appendDateToMapImageFileName,
            serverUrl: state.settings.mapImageServerUrl
        }),
        shallowEqual
    );
    const map = useMap();
    const context = useContext(WorkspaceItemContext);

    const handleScreenshotEvent = useCallback(
        (screenshotCommand: ScreenshotCommand) => {
            if (screenshotCommand.workspaceIndex === context.id && map) {
                if (map.loaded()) {
                    takeMapScreenshot(screenshotCommand);
                } else {
                    map.once('idle', () => takeMapScreenshot(screenshotCommand));
                }
            }
        },
        [context, map]
    );

    useEvent('take-map-screenshot', handleScreenshotEvent);

    const getMapImageBlob = useCallback(
        (format?: MapImageFormat) => {
            const imageFormat = format ?? imageSettings.imageFormat;
            const deckCanvas = context.flightLayerFacade.deckPlugin.getCanvas();
            const mapCanvas = map?.getCanvas();
            if (!deckCanvas || !mapCanvas || !map) {
                return Promise.reject(`Canvas not found for ${deckCanvas}, ${mapCanvas}, ${map}`);
            }

            return new Promise<Blob>(function (resolve, reject) {
                map.once('render', function () {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Failed to create blob - ctx could not be created'));
                        return;
                    }

                    canvas.width = imageSettings.imageWidth || mapCanvas.width;
                    canvas.height = imageSettings.imageHeight || mapCanvas.height;

                    const srcAspect = mapCanvas.width / mapCanvas.height;
                    const dstAspect = canvas.width / canvas.height;

                    let drawWidth,
                        drawHeight,
                        offsetX = 0,
                        offsetY = 0;

                    if (dstAspect > srcAspect) {
                        // Pillarboxing (black bars on sides)
                        drawHeight = canvas.height;
                        drawWidth = drawHeight * srcAspect;
                        offsetX = (canvas.width - drawWidth) / 2;
                    } else {
                        // Letterboxing (black bars on top/bottom)
                        drawWidth = canvas.width;
                        drawHeight = drawWidth / srcAspect;
                        offsetY = (canvas.height - drawHeight) / 2;
                    }

                    // Fill with black background
                    ctx.fillStyle = '#000000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Draw maintaining aspect ratio
                    ctx.drawImage(mapCanvas, offsetX, offsetY, drawWidth, drawHeight);
                    ctx.drawImage(deckCanvas, offsetX, offsetY, drawWidth, drawHeight);

                    canvas.toBlob(function (blob) {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to create blob'));
                        }
                    }, ImageFormat[imageFormat]);
                });
                map.triggerRepaint();
            });
        },
        [imageSettings, map, context]
    );

    const takeMapScreenshot = useCallback(
        async (screenshotCommand: ScreenshotCommand) => {
            let folderHandle: FileSystemDirectoryHandle | undefined;
            try {
                const db = await getDirectoryDb();
                folderHandle = (await db.get('file-handles', MAP_INDEXEDDB_KEY)) as FileSystemDirectoryHandle | undefined;
                const folderHandleValid =
                    folderHandle &&
                    folderHandle.kind === 'directory' &&
                    (await folderHandle.queryPermission({ mode: 'readwrite' })) === 'granted';

                if (!folderHandleValid) {
                    // Make sure the folder handle is undefined if not valid, so the picker starts in default location
                    folderHandle = undefined;
                }
            } catch {
                // fine, we don't have a handle
                folderHandle = undefined;
            }

            if (screenshotCommand.type === ScreenshotCommandType.slideShowAutomatic) {
                // TODO: display error notifications? maybe, maybe not.
                if (screenshotCommand.upload) {
                    await uploadToServer(screenshotCommand, undefined);
                }
                if (screenshotCommand.saveToFile) {
                    if (folderHandle) {
                        await folderDirectSave(folderHandle, screenshotCommand);
                    }
                }
            } else {
                // OLD path
                // ~ml TODO: refactor into a common code path with automatic slideshow screenshot
                const inProgressNotification = dispatch(
                    addNotification({ type: NotificationType.imageSaveInProgress, closable: false, disposeAfterSeconds: 60 })
                );

                try {
                    if (screenshotCommand.type === ScreenshotCommandType.serverUpload) {
                        if (!(await uploadToServer(screenshotCommand, inProgressNotification.payload.id))) {
                            return;
                        }
                    } else if ('showSaveFilePicker' in window && screenshotCommand.type === ScreenshotCommandType.saveAs) {
                        await folderPickerDownload(folderHandle, screenshotCommand);
                    } else if ('showSaveFilePicker' in window && folderHandle) {
                        await folderDirectSave(folderHandle, screenshotCommand);
                    } else {
                        await classicDownload(screenshotCommand);
                    }

                    dispatch(
                        transformNotification({
                            id: inProgressNotification.payload.id,
                            type: NotificationType.generalSuccess,
                            customMessage: 'Map image saved successfully.'
                        })
                    );
                } catch (error) {
                    // User canceled the file picker, handle silently
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        dispatch(dismissNotification({ id: inProgressNotification.payload.id }));
                        return;
                    }
                    console.error(error);
                    dispatch(
                        transformNotification({
                            id: inProgressNotification.payload.id,
                            type: NotificationType.generalError,
                            customMessage: `Failed to save map image: ${error instanceof Error ? error.message : 'Unknown error'}`
                        })
                    );
                }
            }
        },
        [dispatch, map, imageSettings]
    );

    const getFileName = (command: ScreenshotCommand, settings: ImageSettings) => {
        if (command.type === ScreenshotCommandType.slideShowAutomatic) {
            return settings.appendDateToFilename ? command.fileName + dayjs().format('[_]YYYYMMDDHHmmss') : command.fileName;
        }

        return `${command.customFilename ?? settings.filename}${settings.appendDateToFilename ? dayjs().format('[_]YYYYMMDDHHmmss') : ''}`;
    };

    const uploadToServer = useCallback(
        async (screenshotCommand: ScreenshotCommand, inProgressNotificationId?: string) => {
            try {
                if (!imageSettings.serverUrl) {
                    if (inProgressNotificationId) {
                        dispatch(
                            transformNotification({
                                id: inProgressNotificationId,
                                type: NotificationType.generalError,
                                customMessage: `Failed to upload map image to server: sever url not set.`
                            })
                        );
                    }
                    return false;
                }

                const blob = await getMapImageBlob();
                const response = await fetch(
                    `${imageSettings.serverUrl}/${getFileName(screenshotCommand, imageSettings)}${FileExtension[imageSettings.imageFormat]}`,
                    {
                        method: 'PUT',
                        body: blob
                    }
                );
                if (!response.ok) {
                    if (inProgressNotificationId) {
                        dispatch(
                            transformNotification({
                                id: inProgressNotificationId,
                                type: NotificationType.generalError,
                                customMessage: `Failed to upload map image to server: ${response.status} ${response.statusText}`
                            })
                        );
                    }
                }

                return true;
            } catch (error) {
                if (inProgressNotificationId) {
                    dispatch(
                        transformNotification({
                            id: inProgressNotificationId,
                            type: NotificationType.generalError,
                            customMessage: `Failed to upload map image to server: ${error instanceof Error ? error.message : 'Unknown error'}`
                        })
                    );
                }
            }

            return false;
        },
        [getMapImageBlob, imageSettings]
    );

    const classicDownload = useCallback(
        async (command: ScreenshotCommand) => {
            try {
                const blob = await getMapImageBlob();
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = getFileName(command, imageSettings);
                link.click();
                link.remove();
                URL.revokeObjectURL(link.href);
            } catch (error) {
                console.error(error);
                throw new Error(`Failed to download image: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        },
        [getMapImageBlob, imageSettings]
    );

    const folderDirectSave = useCallback(
        async (folderHandle: FileSystemDirectoryHandle, command: ScreenshotCommand) => {
            const filename = `${getFileName(command, imageSettings)}${FileExtension[imageSettings.imageFormat]}`;
            const fileHandle = await folderHandle.getFileHandle(filename, { create: true });
            const blob = await getMapImageBlob();
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
        },
        [getMapImageBlob, imageSettings]
    );

    const folderPickerDownload = useCallback(
        async (folderHandle: FileSystemFileHandle | FileSystemDirectoryHandle | undefined, command: ScreenshotCommand) => {
            const handle = await window.showSaveFilePicker({
                id: MAP_INDEXEDDB_KEY,
                suggestedName: `${getFileName(command, imageSettings)}${FileExtension[imageSettings.imageFormat]}`,
                startIn: folderHandle,
                types: [
                    {
                        description: 'PNG Image',
                        accept: { 'image/png': ['.png'] }
                    },
                    {
                        description: 'JPEG Image',
                        accept: { 'image/jpeg': ['.jpg'] }
                    },
                    {
                        description: 'WebP Image',
                        accept: { 'image/webp': ['.webp'] }
                    }
                ]
            });
            const userPickedExtension = handle.name.substring(handle.name.lastIndexOf('.')).toLowerCase();
            const blob = await getMapImageBlob(MimeType[userPickedExtension]);
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
        },
        [getMapImageBlob, imageSettings]
    );

    return null;
}
